import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Building2,
  FileText,
  ArrowRight,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  XCircle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
  CINEMA_CONTRACT_TEXT,
  CINEMA_CONTRACT_TITLE,
  CINEMA_CONTRACT_VERSION,
  clearCinemaApplication,
  DISTRIBUTION_CONTRACT_TEXT,
  DISTRIBUTION_CONTRACT_TITLE,
  DISTRIBUTION_CONTRACT_VERSION,
  getOnboardingBranch,
  loadCinemaApplication,
  PUBLISHER_CONTRACT_TITLE,
  PUBLISHER_CONTRACT_VERSION,
  saveCinemaApplication,
  type CinemaApplicationPayload,
} from '../lib/partner-contracts';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

type OnboardingStep = 'business' | 'contract' | 'rejected' | 'suspended';

const PARTNER_TYPES = [
  { value: 'indie_creator', label: 'Indie Creator' },
  { value: 'production_company', label: 'Production Company' },
  { value: 'distributor', label: 'Distributor' },
  { value: 'other', label: 'Other' },
] as const;

const COUNTRIES = [
  'Canada',
  'United States',
  'United Kingdom',
  'Australia',
  'New Zealand',
  'Germany',
  'France',
  'Netherlands',
  'Sweden',
  'Norway',
  'Denmark',
  'Finland',
  'Ireland',
  'Austria',
  'Switzerland',
  'Belgium',
  'Portugal',
  'Spain',
  'Italy',
  'Japan',
  'South Korea',
  'Singapore',
  'Hong Kong',
  'Brazil',
  'Mexico',
  'Argentina',
  'Colombia',
  'Chile',
  'India',
  'South Africa',
  'Nigeria',
  'Kenya',
  'Other',
] as const;

// ─── Step 1 Schema ─────────────────────────────────────────────────────────────

const businessSchema = z.object({
  company_name: z.string().min(2, 'Company name must be at least 2 characters'),
  partner_type: z.enum(['indie_creator', 'production_company', 'distributor', 'other'], {
    error: 'Please select a partner type',
  }),
  contact_email: z.string().email('Please enter a valid email'),
  country: z.string().min(1, 'Please select a country'),
});

type BusinessFormData = z.infer<typeof businessSchema>;

const cinemaSchema = z.object({
  theater_name: z.string().min(2, 'Theater name must be at least 2 characters'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State / province is required'),
  country: z.string().min(1, 'Please select a country'),
  address_line1: z.string().min(3, 'Street address is required'),
  contact_email: z.string().email('Please enter a valid email'),
  contact_phone: z.string().optional(),
  screen_count: z.string().optional(),
  max_capacity: z.string().optional(),
  theater_chain: z.string().optional(),
});

type CinemaFormData = z.infer<typeof cinemaSchema>;

const distributorSchema = z.object({
  company_name: z.string().min(2, 'Company name must be at least 2 characters'),
  contact_email: z.string().email('Please enter a valid email'),
  country: z.string().min(1, 'Please select a country'),
  territory_description: z
    .string()
    .min(10, 'Describe the territories and formats you distribute'),
});

type DistributorFormData = z.infer<typeof distributorSchema>;

// ─── Contract Text ─────────────────────────────────────────────────────────────

const CONTRACT_TEXT = `KINORA PUBLISHER AGREEMENT
Version 1.0 — Effective March 2026

This Publisher Agreement ("Agreement") is entered into between Kinora Inc. ("Kinora," "we," "our," or "the Platform") and the publisher registering through this onboarding process ("Publisher," "you," or "your").

By completing the onboarding process and checking the acceptance box below, you agree to be legally bound by this Agreement.

─────────────────────────────────────────────────────────────

1. DEFINITIONS

1.1 "Slices" means the non-monetary virtual credits used on the Kinora platform. One (1) Slice has a notional value of $1.00 USD for accounting purposes within the platform only. Slices are NOT currency, do NOT constitute legal tender, and CANNOT be directly exchanged for cash. Slice values are determined solely by Kinora's internal policies and may change at any time.

1.2 "Content" means any video file, series, documentary, short film, or other media submitted by Publisher for distribution through the Platform.

1.3 "Upload Fee" means the non-refundable fee paid by Publisher before each piece of Content enters the upload pipeline.

1.4 "Publisher Portal" means the web application through which Publisher manages their content, campaigns, and studio identity on Kinora.

─────────────────────────────────────────────────────────────

2. SLICE MECHANICS AND NON-MONETARY NATURE

2.1 Slices are virtual credits awarded to viewers through subscription and promotional programs. Viewers may donate Slices to content they support.

2.2 Slices donated to Publisher's content are credited to Publisher's account. These Slices represent a claim against Kinora's payout pool and may be converted to CAD or USD payouts subject to Kinora's payout policies, minimum thresholds, and applicable law.

2.3 SLICES ARE NOT A PROMISE OF PAYMENT. Kinora makes no guarantee that Slice donations will result in monetary payouts. Payout availability depends on platform revenue, regulatory compliance, and Publisher's completion of Stripe Connect onboarding.

2.4 Publisher acknowledges that Slices do not constitute a financial instrument, security, or investment product under applicable law.

─────────────────────────────────────────────────────────────

3. UPLOAD FEE POLICY

3.1 Publisher must pay a non-refundable Upload Fee before any Content enters the processing pipeline:
   - Feature film, short film (under 40 minutes), or standalone documentary: CAD $100 per title
   - TV series episode: CAD $25–$50 per episode (rate determined by episode duration)
   - Behind-the-scenes, bonus, or supplemental content attached to a paid parent work: Free

3.2 Upload Fees are NON-REFUNDABLE under all circumstances, including:
   (a) Content rejected by Kinora's moderation team
   (b) Publisher's voluntary withdrawal of Content
   (c) Publisher's account termination (voluntary or involuntary)
   (d) Technical failures during the upload process

3.3 The Upload Fee covers: access to Kinora's upload infrastructure, automated content scanning, human moderation review, video encoding and transcoding, and temporary storage during the review period.

3.4 Rejection of Content does not entitle Publisher to a refund. Publisher may re-submit rejected Content as a new submission, subject to payment of a new Upload Fee.

─────────────────────────────────────────────────────────────

4. CONTENT RIGHTS AND LICENSES

4.1 Publisher represents and warrants that they hold all necessary rights, licenses, and permissions to distribute the Content through Kinora, including rights to any music, footage, images, performance rights, and any other third-party intellectual property included in the Content.

4.2 Publisher grants Kinora a non-exclusive, worldwide, royalty-free license to host, encode, store, display, stream, and promote the Content on the Kinora platform and in connection with Kinora's marketing activities.

4.3 Publisher retains all ownership rights to their Content. This Agreement does not transfer copyright or any other ownership interest to Kinora.

4.4 Publisher may request removal of their Content from the Platform at any time. Upon removal, the Content will no longer be available to new viewers; however, existing access rights for viewers who have previously donated Slices to the Content will be preserved for a reasonable transition period.

─────────────────────────────────────────────────────────────

5. CONTENT STANDARDS AND MODERATION

5.1 All Content submitted to Kinora is subject to review by Kinora's moderation team. Kinora reserves the right to reject or remove Content that, in its sole determination:
   (a) Violates applicable laws or regulations
   (b) Contains hate speech, harassment, or discrimination based on protected characteristics
   (c) Depicts non-consensual sexual acts or content involving minors
   (d) Promotes or glorifies illegal activity
   (e) Violates third-party intellectual property rights
   (f) Contains spam, misleading metadata, or artificially inflated engagement

5.2 Rejected Content: Publisher will be notified of the rejection reason. Publisher may submit one (1) appeal per rejected upload. If the appeal is denied, the decision is final and the Content may not be resubmitted without prior written approval from Kinora.

5.3 Kinora may remove published Content at any time if it receives a valid legal notice or determines, upon further review, that the Content violates this Agreement or applicable law. Kinora will provide reasonable notice to Publisher where practicable.

5.4 Repeated violations of this Agreement may result in suspension or termination of Publisher's account at Kinora's discretion.

─────────────────────────────────────────────────────────────

6. REVENUE AND PAYOUT TERMS

6.1 Publishers keep 100% of Slices donated to their Content by viewers. Kinora does not take a percentage cut of Slice donations.

6.2 To receive payouts, Publisher must connect a valid Stripe account through Kinora's Stripe Connect integration. Kinora is not responsible for Stripe's service terms, payout schedules, or account restrictions.

6.3 Payout eligibility is subject to minimum threshold amounts, identity verification, and compliance with applicable tax and financial regulations. Kinora may withhold payouts pending resolution of disputes, compliance reviews, or platform investigations.

6.4 Publisher is responsible for all applicable taxes on income received through the Platform, including income tax, GST/HST, VAT, and any other taxes applicable in their jurisdiction.

6.5 Crowdfunding Campaign Fees: Publisher pays a non-refundable activation fee of CAD $25–$50 per crowdfunding campaign at the time of campaign activation. Draft campaigns are free. Slices pledged by backers are refunded if a campaign is cancelled.

─────────────────────────────────────────────────────────────

7. PLATFORM RULES

7.1 Publisher agrees not to:
   (a) Create multiple Publisher accounts for the same company or entity
   (b) Artificially inflate view counts, Slice donations, or campaign pledges
   (c) Use the Platform to distribute Content that violates any applicable law
   (d) Share login credentials with unauthorized parties
   (e) Attempt to circumvent any Platform security measures
   (f) Engage in any activity that disrupts or impairs the Platform's operation

7.2 Publisher is responsible for maintaining the security of their account credentials.

─────────────────────────────────────────────────────────────

8. TERM AND TERMINATION

8.1 This Agreement begins on the date Publisher accepts it and continues until terminated.

8.2 Publisher may terminate this Agreement at any time by requesting account deletion through the Publisher Portal Settings.

8.3 Kinora may suspend or terminate Publisher's access immediately if Publisher materially breaches this Agreement, or if required by applicable law.

8.4 Upon termination: (a) Publisher's studio page will be unpublished; (b) Content will be soft-deleted (vaulted) and remain accessible only to viewers with existing access; (c) any pending Slice payouts will be processed subject to minimum thresholds; (d) pending campaign Slices will be refunded to backers.

─────────────────────────────────────────────────────────────

9. LIMITATION OF LIABILITY

9.1 TO THE MAXIMUM EXTENT PERMITTED BY LAW, SLICETV'S TOTAL LIABILITY FOR ANY CLAIM ARISING UNDER THIS AGREEMENT IS LIMITED TO THE UPLOAD FEES PAID BY PUBLISHER IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.

9.2 SLICETV IS NOT LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST REVENUE, LOST PROFITS, OR LOSS OF DATA.

─────────────────────────────────────────────────────────────

10. GOVERNING LAW

This Agreement is governed by the laws of the Province of Ontario, Canada, and the federal laws of Canada applicable therein, without regard to conflict of law principles. Any disputes shall be resolved in the courts of Ontario, Canada.

─────────────────────────────────────────────────────────────

11. CHANGES TO THIS AGREEMENT

Kinora may update this Agreement from time to time. Publisher will be notified of material changes and must re-accept the updated Agreement before continuing to use the Publisher Portal. Prior acceptance records are preserved for legal audit purposes.

─────────────────────────────────────────────────────────────

12. ENTIRE AGREEMENT

This Agreement constitutes the entire agreement between Publisher and Kinora regarding the subject matter herein and supersedes all prior agreements, representations, or understandings.

─────────────────────────────────────────────────────────────

If you have questions about this Agreement, contact us at publishers@kinora.tv

Kinora Inc.
Toronto, Ontario, Canada`;

// ─── Component ─────────────────────────────────────────────────────────────────

export default function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, publisher, publisherLoading, refreshPublisher } = useAuth();
  const branch = getOnboardingBranch(publisher);

  const stepParam = searchParams.get('step');
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(() => {
    if (stepParam === 'contract') return 'contract';
    if (stepParam === 'rejected') return 'rejected';
    if (stepParam === 'suspended') return 'suspended';
    return 'business';
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Contract step state
  const [hasScrolled, setHasScrolled] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Redirect if already fully onboarded
  useEffect(() => {
    if (publisher?.contract_signed) {
      const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
      navigate(from || '/dashboard', { replace: true });
    }
  }, [publisher, navigate, location.state]);

  useEffect(() => {
    if (!publisherLoading && user && !publisher) {
      navigate('/select-partner-type', { replace: true });
    }
  }, [publisherLoading, user, publisher, navigate]);

  const contractMeta =
    branch === 'cinema'
      ? {
          title: CINEMA_CONTRACT_TITLE,
          text: CINEMA_CONTRACT_TEXT,
          version: CINEMA_CONTRACT_VERSION,
          agreeLabel:
            'I have read and agree to the Kinora Cinema Exhibition Agreement. I understand ticket prices and theatrical windows are set by publishers on the Theatre Marketplace.',
        }
      : branch === 'distributor'
        ? {
            title: DISTRIBUTION_CONTRACT_TITLE,
            text: DISTRIBUTION_CONTRACT_TEXT,
            version: DISTRIBUTION_CONTRACT_VERSION,
            agreeLabel:
              'I have read and agree to the Kinora Distribution Partner Agreement and confirm I have authority in the territories I described.',
          }
        : {
            title: PUBLISHER_CONTRACT_TITLE,
            text: CONTRACT_TEXT,
            version: PUBLISHER_CONTRACT_VERSION,
            agreeLabel:
              'I have read and agree to the Kinora Publisher Agreement (Version 1.0). I understand that upload fees are non-refundable, that Slices are non-monetary virtual credits, and that my content is subject to moderation review.',
          };

  // ─── Step 1 Form ───────────────────────────────────────────────────────────

  const form = useForm<BusinessFormData>({
    resolver: zodResolver(businessSchema),
    defaultValues: {
      company_name: publisher?.company_name || '',
      partner_type: (publisher?.partner_type as any) || undefined,
      contact_email: publisher?.contact_email || user?.email || '',
      country: publisher?.country || 'Canada',
    },
  });

  const cinemaForm = useForm<CinemaFormData>({
    resolver: zodResolver(cinemaSchema),
    defaultValues: {
      theater_name: publisher?.company_name || '',
      city: publisher?.city || '',
      state: publisher?.state || '',
      country: publisher?.country || 'Canada',
      address_line1: publisher?.address_line1 || '',
      contact_email: publisher?.contact_email || user?.email || '',
      contact_phone: publisher?.contact_phone || '',
      screen_count: '',
      max_capacity: '',
      theater_chain: '',
    },
  });

  const distributorForm = useForm<DistributorFormData>({
    resolver: zodResolver(distributorSchema),
    defaultValues: {
      company_name: publisher?.company_name || '',
      contact_email: publisher?.contact_email || user?.email || '',
      country: publisher?.country || 'Canada',
      territory_description: publisher?.description || '',
    },
  });

  // Pre-fill from existing publisher row when it loads
  useEffect(() => {
    if (publisher) {
      form.reset({
        company_name: publisher.company_name || '',
        partner_type: (publisher.partner_type as any) || undefined,
        contact_email: publisher.contact_email || user?.email || '',
        country: publisher.country || 'Canada',
      });
      cinemaForm.reset({
        theater_name: publisher.company_name || '',
        city: publisher.city || '',
        state: publisher.state || '',
        country: publisher.country || 'Canada',
        address_line1: publisher.address_line1 || '',
        contact_email: publisher.contact_email || user?.email || '',
        contact_phone: publisher.contact_phone || '',
        screen_count: '',
        max_capacity: '',
        theater_chain: '',
      });
      distributorForm.reset({
        company_name: publisher.company_name || '',
        contact_email: publisher.contact_email || user?.email || '',
        country: publisher.country || 'Canada',
        territory_description: publisher.description || '',
      });
      // If business info already saved, skip to contract
      if (publisher.application_completed && !publisher.contract_signed && stepParam !== 'business') {
        setCurrentStep('contract');
      }
    }
  }, [publisher, user, form, cinemaForm, distributorForm, stepParam]);

  const handleBusinessSubmit = async (data: BusinessFormData) => {
    if (!user) return;
    setSubmitting(true);
    setError('');

    try {
      // Generate brand_slug from company name
      const slug = data.company_name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

      if (publisher?.id) {
        // Update existing row
        const { error: updateError } = await supabase
          .from('publishers')
          .update({
            company_name: data.company_name,
            partner_type: data.partner_type,
            contact_email: data.contact_email,
            country: data.country,
            application_completed: true,
            application_completed_at: new Date().toISOString(),
          })
          .eq('id', publisher.id);

        if (updateError) throw updateError;
      } else {
        // Create new row
        // Check uniqueness of slug
        let finalSlug = slug;
        const { data: existing } = await supabase
          .from('publishers')
          .select('id')
          .eq('brand_slug', slug)
          .maybeSingle();

        if (existing) {
          finalSlug = `${slug}-${Date.now()}`;
        }

        const { error: insertError } = await supabase
          .from('publishers')
          .insert({
            auth_user_id: user.id,
            company_name: data.company_name,
            legal_entity_name: data.company_name,
            // partner_type is the new field; business_type is the old constrained field
            // and is intentionally omitted here (nullable, so NULL passes the constraint)
            partner_type: data.partner_type,
            contact_email: data.contact_email,
            country: data.country,
            brand_slug: finalSlug,
            application_completed: true,
            application_completed_at: new Date().toISOString(),
            is_active: false,
            is_verified: false,
            status: 'pending_review',
          });

        if (insertError) throw insertError;
      }

      await refreshPublisher();
      setCurrentStep('contract');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      console.error('Business info submit error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCinemaSubmit = async (data: CinemaFormData) => {
    if (!user || !publisher?.id) return;
    setSubmitting(true);
    setError('');

    try {
      const payload: CinemaApplicationPayload = {
        theater_name: data.theater_name,
        city: data.city,
        state: data.state,
        country: data.country,
        address_line1: data.address_line1,
        contact_email: data.contact_email,
        contact_phone: data.contact_phone || '',
        screen_count: data.screen_count ? Number(data.screen_count) : null,
        max_capacity: data.max_capacity ? Number(data.max_capacity) : null,
        theater_chain: data.theater_chain || '',
      };
      saveCinemaApplication(payload);

      const { error: updateError } = await supabase
        .from('publishers')
        .update({
          company_name: data.theater_name,
          legal_entity_name: data.theater_name,
          city: data.city,
          state: data.state,
          country: data.country,
          address_line1: data.address_line1,
          contact_email: data.contact_email,
          contact_phone: data.contact_phone || null,
          application_completed: true,
          application_completed_at: new Date().toISOString(),
        })
        .eq('id', publisher.id);

      if (updateError) throw updateError;

      await refreshPublisher();
      setCurrentStep('contract');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: unknown) {
      console.error('Cinema application submit error:', err);
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDistributorSubmit = async (data: DistributorFormData) => {
    if (!user || !publisher?.id) return;
    setSubmitting(true);
    setError('');

    try {
      const { error: updateError } = await supabase
        .from('publishers')
        .update({
          company_name: data.company_name,
          legal_entity_name: data.company_name,
          contact_email: data.contact_email,
          country: data.country,
          description: data.territory_description,
          application_completed: true,
          application_completed_at: new Date().toISOString(),
        })
        .eq('id', publisher.id);

      if (updateError) throw updateError;

      await refreshPublisher();
      setCurrentStep('contract');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: unknown) {
      console.error('Distributor application submit error:', err);
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Step 2: Contract scroll tracking ─────────────────────────────────────

  const handleContractScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    if (nearBottom && !hasScrolled) setHasScrolled(true);
  };

  const handleContractSubmit = async () => {
    if (!publisher?.id || !agreed) return;
    setSubmitting(true);
    setError('');

    try {
      const { error: updateError } = await supabase
        .from('publishers')
        .update({
          contract_signed: true,
          contract_signed_at: new Date().toISOString(),
          contract_signed_date: new Date().toISOString(),
          contract_version: contractMeta.version,
          contract_status: 'active',
          onboarding_completed_at: new Date().toISOString(),
        })
        .eq('id', publisher.id);

      if (updateError) throw updateError;

      if (branch === 'cinema') {
        const cinemaData = loadCinemaApplication();
        if (!cinemaData) {
          throw new Error('Cinema application data missing. Please complete step 1 again.');
        }
        const { error: theaterError } = await supabase.rpc('upsert_cinema_theater', {
          p_theater_name: cinemaData.theater_name,
          p_city: cinemaData.city,
          p_state: cinemaData.state,
          p_country: cinemaData.country,
          p_contact_email: cinemaData.contact_email,
          p_contact_phone: cinemaData.contact_phone || undefined,
          p_address_line1: cinemaData.address_line1,
          p_screen_count: cinemaData.screen_count ?? undefined,
          p_max_capacity: cinemaData.max_capacity ?? undefined,
          p_theater_chain: cinemaData.theater_chain || undefined,
        });
        if (theaterError) throw theaterError;
        clearCinemaApplication();
      }

      await refreshPublisher();
      toast.success(
        branch === 'cinema'
          ? 'Welcome! Your cinema partner account is ready.'
          : branch === 'distributor'
            ? 'Welcome! Your distribution partner account is ready.'
            : 'Welcome to Kinora! Your publisher account is ready.'
      );
      navigate('/dashboard');
    } catch (err: unknown) {
      console.error('Contract submit error:', err);
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Blocked States ────────────────────────────────────────────────────────

  if (currentStep === 'rejected') {
    return (
      <div className='min-h-screen flex items-center justify-center bg-background p-6'>
        <Card className='w-full max-w-md text-center'>
          <CardHeader>
            <div className='flex justify-center mb-4'>
              <XCircle className='h-12 w-12 text-destructive' />
            </div>
            <CardTitle>Application Not Approved</CardTitle>
            <CardDescription>
              {publisher?.rejection_reason ||
                'Your publisher application was not approved at this time.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className='text-sm text-muted-foreground'>
              If you believe this is an error, please contact us at{' '}
              <a
                href='mailto:publishers@slicetv.app'
                className='text-primary hover:underline'
              >
                publishers@slicetv.app
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (currentStep === 'suspended') {
    return (
      <div className='min-h-screen flex items-center justify-center bg-background p-6'>
        <Card className='w-full max-w-md text-center'>
          <CardHeader>
            <div className='flex justify-center mb-4'>
              <AlertTriangle className='h-12 w-12 text-yellow-500' />
            </div>
            <CardTitle>Account Suspended</CardTitle>
            <CardDescription>
              Your publisher account has been suspended.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className='text-sm text-muted-foreground'>
              Please contact us at{' '}
              <a
                href='mailto:publishers@slicetv.app'
                className='text-primary hover:underline'
              >
                publishers@slicetv.app
              </a>{' '}
              to resolve this.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Step Indicator ────────────────────────────────────────────────────────

  const steps = [
    { id: 'business', label: 'Business Info', icon: Building2 },
    { id: 'contract', label: 'Agreement', icon: FileText },
  ];

  const stepIndex = currentStep === 'business' ? 0 : 1;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className='dark min-h-screen bg-background flex flex-col items-center justify-start py-12 px-4'>
      {/* Logo / Brand */}
      <div className='mb-8 text-center'>
        <h1 className='text-2xl font-bold tracking-tight'>Kinora Publisher</h1>
        <p className='text-sm text-muted-foreground mt-1'>
          Set up your publisher account
        </p>
      </div>

      {/* Step indicator */}
      <div className='w-full max-w-lg mb-8'>
        <div className='flex items-center justify-center gap-0'>
          {steps.map((step, idx) => {
            const Icon = step.icon;
            const isActive = idx === stepIndex;
            const isDone = idx < stepIndex;

            return (
              <div key={step.id} className='flex items-center'>
                <div className='flex flex-col items-center gap-1.5'>
                  <div
                    className={`flex h-9 w-9 items-center justify-center rounded-full border-2 transition-all duration-200 ${
                      isDone
                        ? 'border-primary bg-primary text-primary-foreground'
                        : isActive
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-muted-foreground/30 bg-muted text-muted-foreground'
                    }`}
                  >
                    {isDone ? (
                      <CheckCircle2 className='h-4 w-4' />
                    ) : (
                      <Icon className='h-4 w-4' />
                    )}
                  </div>
                  <span
                    className={`text-xs font-medium ${
                      isActive ? 'text-primary' : isDone ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div
                    className={`h-0.5 w-20 mx-3 mb-5 transition-all duration-200 ${
                      stepIndex > idx ? 'bg-primary' : 'bg-muted-foreground/20'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Error alert */}
      {error && (
        <div className='w-full max-w-lg mb-4'>
          <Alert variant='destructive'>
            <AlertTriangle className='h-4 w-4' />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      )}

      {/* ── Step 1: Application ─────────────────────────────────────────── */}
      {currentStep === 'business' && branch === 'cinema' && (
        <Card className='w-full max-w-lg'>
          <CardHeader>
            <CardTitle className='text-xl'>Cinema Application</CardTitle>
            <CardDescription>
              Tell us about your theater location. This links your cinema partner account to a
              Kinora marketplace exhibitor profile.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...cinemaForm}>
              <form
                onSubmit={cinemaForm.handleSubmit(handleCinemaSubmit)}
                className='space-y-5'
              >
                <FormField
                  control={cinemaForm.control}
                  name='theater_name'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Theater / venue name</FormLabel>
                      <FormControl>
                        <Input placeholder='e.g. Royal Cinema' {...field} autoFocus />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={cinemaForm.control}
                  name='theater_chain'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Chain (optional)</FormLabel>
                      <FormControl>
                        <Input placeholder='Independent or chain name' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={cinemaForm.control}
                  name='address_line1'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Street address</FormLabel>
                      <FormControl>
                        <Input placeholder='123 Main Street' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className='grid gap-4 sm:grid-cols-2'>
                  <FormField
                    control={cinemaForm.control}
                    name='city'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={cinemaForm.control}
                    name='state'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>State / province</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={cinemaForm.control}
                  name='country'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder='Select country' />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className='max-h-64 overflow-y-auto'>
                          {COUNTRIES.map(c => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className='grid gap-4 sm:grid-cols-2'>
                  <FormField
                    control={cinemaForm.control}
                    name='screen_count'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Screens</FormLabel>
                        <FormControl>
                          <Input type='number' min={1} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={cinemaForm.control}
                    name='max_capacity'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max capacity</FormLabel>
                        <FormControl>
                          <Input type='number' min={1} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={cinemaForm.control}
                  name='contact_email'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact email</FormLabel>
                      <FormControl>
                        <Input type='email' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={cinemaForm.control}
                  name='contact_phone'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact phone (optional)</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type='submit' className='w-full' disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                      Saving...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className='h-4 w-4 ml-2' />
                    </>
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}

      {currentStep === 'business' && branch === 'distributor' && (
        <Card className='w-full max-w-lg'>
          <CardHeader>
            <CardTitle className='text-xl'>Distributor Application</CardTitle>
            <CardDescription>
              Tell us about your distribution company and the territories you serve.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...distributorForm}>
              <form
                onSubmit={distributorForm.handleSubmit(handleDistributorSubmit)}
                className='space-y-5'
              >
                <FormField
                  control={distributorForm.control}
                  name='company_name'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company name</FormLabel>
                      <FormControl>
                        <Input placeholder='e.g. North Star Distribution' {...field} autoFocus />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={distributorForm.control}
                  name='contact_email'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact email</FormLabel>
                      <FormControl>
                        <Input type='email' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={distributorForm.control}
                  name='country'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Headquarters country</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder='Select country' />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className='max-h-64 overflow-y-auto'>
                          {COUNTRIES.map(c => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={distributorForm.control}
                  name='territory_description'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Territories & formats</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='e.g. Canada theatrical + EST; Nordic SVOD'
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type='submit' className='w-full' disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                      Saving...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className='h-4 w-4 ml-2' />
                    </>
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}

      {currentStep === 'business' && branch === 'publisher' && (
        <Card className='w-full max-w-lg'>
          <CardHeader>
            <CardTitle className='text-xl'>Business Information</CardTitle>
            <CardDescription>
              Tell us about your company or studio. This takes about 2 minutes.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleBusinessSubmit)}
                className='space-y-5'
              >
                {/* Company Name */}
                <FormField
                  control={form.control}
                  name='company_name'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company / Studio Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='e.g. Moonshot Films'
                          {...field}
                          autoFocus
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Partner Type */}
                <FormField
                  control={form.control}
                  name='partner_type'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Partner Type</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder='Select partner type' />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PARTNER_TYPES.map((pt) => (
                            <SelectItem key={pt.value} value={pt.value}>
                              {pt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Contact Email */}
                <FormField
                  control={form.control}
                  name='contact_email'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Email</FormLabel>
                      <FormControl>
                        <Input
                          type='email'
                          placeholder='contact@yourcompany.com'
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Country */}
                <FormField
                  control={form.control}
                  name='country'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country / Jurisdiction</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder='Select country' />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className='max-h-64 overflow-y-auto'>
                          {COUNTRIES.map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type='submit'
                  className='w-full'
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                      Saving...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className='h-4 w-4 ml-2' />
                    </>
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}

      {/* ── Step 2: Contract ──────────────────────────────────────────────── */}
      {currentStep === 'contract' && (
        <Card className='w-full max-w-2xl'>
          <CardHeader>
            <CardTitle className='text-xl'>{contractMeta.title}</CardTitle>
            <CardDescription>
              Read the agreement below. Scroll to the bottom to enable the
              acceptance checkbox.
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-5'>
            {/* Scrollable Contract */}
            <div
              className='relative rounded-md border border-border'
              style={{ height: '420px' }}
            >
              <div
                ref={scrollRef}
                onScroll={handleContractScroll}
                className='h-full overflow-y-auto p-5 text-sm leading-relaxed text-muted-foreground font-mono'
                style={{ whiteSpace: 'pre-wrap' }}
              >
                {contractMeta.text}
              </div>
              {/* Fade overlay at bottom when not yet scrolled */}
              {!hasScrolled && (
                <div className='pointer-events-none absolute bottom-0 left-0 right-0 h-16 rounded-b-md bg-gradient-to-t from-card to-transparent' />
              )}
            </div>

            {/* Scroll prompt */}
            {!hasScrolled && (
              <p className='text-xs text-muted-foreground text-center'>
                Scroll to the bottom of the agreement to continue
              </p>
            )}

            {/* Agreement checkbox */}
            <div
              className={`flex items-start gap-3 rounded-md border p-4 transition-all duration-200 ${
                hasScrolled
                  ? 'border-border bg-muted/20'
                  : 'border-muted/30 bg-muted/5 opacity-50 pointer-events-none'
              }`}
            >
              <Checkbox
                id='agree'
                checked={agreed}
                onCheckedChange={(checked) => setAgreed(checked === true)}
                disabled={!hasScrolled}
                className='mt-0.5'
              />
              <label
                htmlFor='agree'
                className='text-sm cursor-pointer leading-relaxed'
              >
                {contractMeta.agreeLabel}
              </label>
            </div>

            {/* Submit */}
            <Button
              className='w-full'
              disabled={!agreed || submitting}
              onClick={handleContractSubmit}
            >
              {submitting ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  Completing Setup...
                </>
              ) : (
                <>
                  <CheckCircle2 className='h-4 w-4 mr-2' />
                  Complete Setup
                </>
              )}
            </Button>

            {/* Back link */}
            <button
              type='button'
              onClick={() => setCurrentStep('business')}
              className='w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer'
            >
              ← Back to Business Info
            </button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
